/* eslint-disable @typescript-eslint/no-unsafe-member-access, react-refresh/only-export-components, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  Ref,
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import {
  createSeriesMarkers,
  LineSeries,
  SeriesMarker,
  SeriesDefinition,
  SeriesType,
  PriceScaleOptions,
} from 'lightweight-charts';
import { Context } from './Chart';

export const SeriesContext = createContext(undefined as any);

type SeriesProps<T extends SeriesType> = {
  children: React.ReactNode | React.ReactNode[];
  data: any[];
  markers?: SeriesMarker<unknown>[];
  name: string;
  type?: SeriesDefinition<T>;
  priceScaleOptions?: PriceScaleOptions;
};

const Series = forwardRef(
  <T extends SeriesType>(props: SeriesProps<T>, ref: Ref<unknown>) => {
    const parent = useContext(Context);
    const { children, data, markers, name, type, ...rest } = props;
    const context = useRef({
      series: undefined as any, // TODO: fix types
      api() {
        if (this.series != null) {
          return this.series;
        }
        this.series = parent.api().addSeries(type ?? LineSeries, rest);
        console.debug(`setData (api) ${name}... ${data?.length}`);
        this.series.setData(data);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        createSeriesMarkers(this.series, markers ?? []);
        return this.series;
      },
      destroy() {
        if (this.series != null) {
          console.debug(`removeSeries ${name}`);
          parent.removeSeries(this.series);
          this.series = null;
        }
      },
    });

    // Rely on imperative update API called in HiroChart rather than the below.
    // Calling `setData` below sets the viewport and shouldn't
    useLayoutEffect(() => {
      const api = context.current.api();
      api.setData(data);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      createSeriesMarkers(context.current.series, markers ?? []);
      api.applyOptions(rest);
    }, [rest, data, markers]);

    useLayoutEffect(() => {
      const currentRef = context.current;
      currentRef.api();
      return () => {
        console.debug(`destroying ${name}...`);
        currentRef.destroy();
      };
    }, [name]);

    useLayoutEffect(() => {
      context.current.api().applyOptions(rest);
      if (props.priceScaleOptions) {
        context.current
          .api()
          .priceScale()
          .applyOptions(props.priceScaleOptions);
      }
    });

    // Useful if we want to utilize the addData API
    useImperativeHandle(ref, () => context.current.api(), []);

    return (
      <SeriesContext.Provider value={context.current}>
        {children}
      </SeriesContext.Provider>
    );
  },
);

Series.displayName = 'Series';

export default Series;
